import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone, Share } from 'lucide-react';
import { usePWAInstall } from '../hooks/usePWAInstall';

const STORAGE_KEY = 'kansha_pwa_dismissed_at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function detectIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
}

export default function PWAInstallBanner() {
  const { canInstall, isInstalled, install } = usePWAInstall();
  const [show, setShow] = useState(false);
  const [isIOS] = useState(detectIOS());
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    if (isInstalled || isStandalone()) return;

    // Check if dismissed within TTL
    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    if (dismissedAt && Date.now() - parseInt(dismissedAt, 10) < DISMISS_TTL_MS) {
      return;
    }

    // Show after delay (30s) so user has engaged with app
    const timer = setTimeout(() => {
      // Show if: Chrome can install OR iOS Safari (manual instructions)
      if (canInstall || isIOS) {
        setShow(true);
      }
    }, 30000);

    return () => clearTimeout(timer);
  }, [canInstall, isInstalled, isIOS]);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
    setShow(false);
  };

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }
    const accepted = await install();
    if (accepted) {
      setShow(false);
    }
  };

  if (!show) return null;

  return (
    <>
      {/* Main banner */}
      <div
        className="fixed bottom-4 right-4 z-[60] max-w-sm bg-gradient-to-br from-[#1A4D2E] to-[#143d24] text-white rounded-xl shadow-2xl border border-emerald-500/30 overflow-hidden animate-in slide-in-from-bottom-4 duration-500"
        data-testid="pwa-install-banner"
      >
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="bg-emerald-500/20 p-2 rounded-lg flex-shrink-0">
              <Smartphone size={22} className="text-emerald-300" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-sm mb-0.5" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Install Kansha Finance
              </h3>
              <p className="text-xs text-stone-200 leading-relaxed">
                Akses lebih cepat dari home screen tanpa buka browser. Bekerja juga saat offline (data tersimpan).
              </p>
            </div>
            <button
              onClick={handleDismiss}
              className="text-stone-300 hover:text-white p-1 -mt-1 -mr-1 rounded transition-colors"
              data-testid="pwa-dismiss"
            >
              <X size={16} />
            </button>
          </div>

          <div className="flex gap-2 mt-3">
            <button
              onClick={handleInstall}
              className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-semibold py-2 px-3 rounded-md transition-colors"
              data-testid="pwa-install-action"
            >
              <Download size={14} />
              {isIOS ? 'Cara Install' : 'Install Sekarang'}
            </button>
            <button
              onClick={handleDismiss}
              className="px-3 text-xs text-stone-300 hover:text-white"
              data-testid="pwa-later"
            >
              Nanti
            </button>
          </div>
        </div>
      </div>

      {/* iOS install guide modal */}
      {showIOSGuide && (
        <div
          className="fixed inset-0 z-[70] bg-black/60 flex items-end justify-center sm:items-center p-4"
          onClick={() => setShowIOSGuide(false)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 animate-in slide-in-from-bottom-4 duration-300"
            onClick={(e) => e.stopPropagation()}
            data-testid="pwa-ios-guide"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Install di iOS
              </h3>
              <button onClick={() => setShowIOSGuide(false)} className="p-1 hover:bg-stone-100 rounded">
                <X size={20} />
              </button>
            </div>

            <p className="text-sm text-stone-600 mb-4">
              iOS Safari belum mendukung 1-klik install. Ikuti 3 langkah ini:
            </p>

            <ol className="space-y-3">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#1A4D2E] text-white text-sm font-bold flex items-center justify-center">1</span>
                <div className="flex-1">
                  <p className="text-sm text-[#0F172A]">Tap tombol <Share size={14} className="inline mx-0.5 text-blue-600" /> <strong>Share</strong> di bawah Safari</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#1A4D2E] text-white text-sm font-bold flex items-center justify-center">2</span>
                <div className="flex-1">
                  <p className="text-sm text-[#0F172A]">Pilih <strong>"Add to Home Screen"</strong> / <strong>"Tambah ke Layar Utama"</strong></p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#1A4D2E] text-white text-sm font-bold flex items-center justify-center">3</span>
                <div className="flex-1">
                  <p className="text-sm text-[#0F172A]">Tap <strong>"Add"</strong> / <strong>"Tambah"</strong>. Selesai!</p>
                </div>
              </li>
            </ol>

            <button
              onClick={() => { setShowIOSGuide(false); handleDismiss(); }}
              className="w-full mt-5 bg-[#1A4D2E] text-white py-2.5 rounded-md font-medium hover:bg-[#143d24]"
            >
              Mengerti
            </button>
          </div>
        </div>
      )}
    </>
  );
}
